import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatDate, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import { Receipt, Search, ChevronLeft, ChevronRight, FileText, Building2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

function InvoiceRow({ invoice, onView, onRecordPayment }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{invoice.invoice_number || invoice.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(invoice.date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{invoice.sale?.sale_number || invoice.sale_number || '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{invoice.customer?.name || invoice.sale?.customer?.name || '-'}</span>
        </div>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(invoice.amount || invoice.total)}</span>
      </td>
      <td>
        <span className="text-sm text-success-600">{formatCurrency(invoice.paid)}</span>
      </td>
      <td>
        <span className="text-sm text-danger-600">{formatCurrency((invoice.amount || invoice.total) - invoice.paid)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(invoice.due_date)}</span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(invoice.status)}`}>{statusLabel(invoice.status)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onView(invoice)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
            <FileText className="h-4 w-4" />
          </button>
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <button onClick={() => onRecordPayment(invoice)} className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded">
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function SalesInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', date_from: '', date_to: '' });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: '', notes: '' });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, filters]);

  useEffect(() => {
    if (showRecordPaymentModal) fetchPaymentMethods();
  }, [showRecordPaymentModal]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/sales-invoices', { params });
      if (res.data.success) {
        setInvoices(res.data.data?.invoices || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await api.get('/payment-methods');
      setPaymentMethods(res.data.data?.payment_methods || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    setSaving(true);
    try {
      const res = await api.post(`/sales-invoices/${selectedInvoice.id}/pay`, paymentForm);
      if (res.data.success) {
        toast.success('Pembayaran berhasil dicatat');
        setShowRecordPaymentModal(false);
        setSelectedInvoice(null);
        setPaymentForm({ amount: '', method: '', notes: '' });
        fetchInvoices();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const openRecordPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    const balance = (invoice.amount || invoice.total) - invoice.paid;
    setPaymentForm({ amount: balance.toString(), method: '', notes: '' });
    setShowRecordPaymentModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Sales Invoices</h1>
          <p className="page-subtitle">Kelola invoice penjualan</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor invoice, nomor penjualan..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchInvoices()}
              />
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="unpaid">Belum Dibayar</option>
                <option value="partial">Sebagian</option>
                <option value="paid">Lunas</option>
                <option value="overdue">Jatuh Tempo</option>
              </select>
            </div>
            <div>
              <input type="date" className="input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <input type="date" className="input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchInvoices} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Receipt className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada invoice penjualan.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nomor Invoice</th>
                    <th>Tanggal</th>
                    <th>Nomor Penjualan</th>
                    <th>Pelanggan</th>
                    <th>Jumlah</th>
                    <th>Dibayar</th>
                    <th>Sisa</th>
                    <th>Jatuh Tempo</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <InvoiceRow
                      key={inv.id}
                      invoice={inv}
                      onView={() => { setSelectedInvoice(inv); setShowDetailsModal(true); }}
                      onRecordPayment={openRecordPaymentModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedInvoice && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Sales Invoice</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-semibold">Nomor Invoice:</label>
                  <p className="text-gray-900 dark:text-white">{selectedInvoice.invoice_number || selectedInvoice.number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Tanggal:</label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(selectedInvoice.date)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Nomor Penjualan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedInvoice.sale?.sale_number || selectedInvoice.sale_number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Status:</label>
                  <span className={`badge ${statusBadgeClass(selectedInvoice.status)}`}>{statusLabel(selectedInvoice.status)}</span>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Pelanggan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedInvoice.customer?.name || selectedInvoice.sale?.customer?.name}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Jatuh Tempo:</label>
                  <p className="text-gray-900 dark:text-white">{formatDate(selectedInvoice.due_date)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Total:</label>
                  <p className="text-gray-900 dark:text-white">{formatCurrency(selectedInvoice.amount || selectedInvoice.total)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Dibayar:</label>
                  <p className="text-success-600">{formatCurrency(selectedInvoice.paid)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Sisa:</label>
                  <p className="text-danger-600">{formatCurrency((selectedInvoice.amount || selectedInvoice.total) - selectedInvoice.paid)}</p>
                </div>
              </div>

              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-3">Item Invoice</h4>
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Deskripsi</th>
                          <th>Qty</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.description || item.product?.name || '-'}</td>
                            <td>{item.qty}</td>
                            <td>{formatCurrency(item.unit_price || item.price)}</td>
                            <td>{formatCurrency(item.subtotal || (item.qty * (item.unit_price || item.price)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button onClick={() => { setShowDetailsModal(false); setSelectedInvoice(null); }} className="btn-primary flex-1">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showRecordPaymentModal && selectedInvoice && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Record Payment</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Invoice: {selectedInvoice.invoice_number || selectedInvoice.number}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sisa: {formatCurrency((selectedInvoice.amount || selectedInvoice.total) - selectedInvoice.paid)}</p>
            </div>
            <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
              <div>
                <label className="label">Jumlah Pembayaran <span className="text-danger-600">*</span></label>
                <input type="number" className="input" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} min="0" step="0.01" max={(selectedInvoice.amount || selectedInvoice.total) - selectedInvoice.paid} required />
              </div>
              <div>
                <label className="label">Metode Pembayaran <span className="text-danger-600">*</span></label>
                <select className="input" value={paymentForm.method} onChange={(e) => setPaymentForm({...paymentForm, method: e.target.value})} required>
                  <option value="">Pilih Metode</option>
                  {paymentMethods.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
                </select>
              </div>
              <div>
                <label className="label">Catatan</label>
                <textarea className="input min-h-[60px]" value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} placeholder="Catatan pembayaran..." rows={2} />
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowRecordPaymentModal(false); setSelectedInvoice(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Memproses...' : 'Record Payment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}